// 我的文章 ARTmy(/me/articles;🔒 Authed)。
//   - v_article_mine(useMyArticles)含 draft/published/removed 全状态
//   - 状态筛 segment(全部/草稿/已发布/已下架)
//   - 行点击分流:published → 详情;draft/removed → 编辑器
//   - 行内软删(delete_article);FAB「写文章」→ 编辑器
// 契约源:docs/ui/user-page-flows/article.md ARTmy + docs/api-list/user/articles.md。

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import {
  useMyArticles,
  useDeleteArticle,
  type ArticleStatus,
} from '../_lib/article-api'

type Filter = 'all' | ArticleStatus

const TABS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: '全部' },
  { key: 'draft', label: '草稿' },
  { key: 'published', label: '已发布' },
  { key: 'removed', label: '已下架' },
]

const STATUS_BADGE: Record<ArticleStatus, { label: string; cls: string }> = {
  draft: { label: '草稿', cls: 'bg-[#F1F2F4] text-[#6B7280]' },
  published: { label: '已发布', cls: 'bg-[#ebfaf6] text-[#0f7969]' },
  removed: { label: '已下架', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
}

function fmtTime(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function MyArticlesPage() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('all')
  const { data: articles, isLoading } = useMyArticles(
    filter === 'all' ? {} : { status: filter },
  )
  const deleteMut = useDeleteArticle()
  const [confirmId, setConfirmId] = useState<string | null>(null)

  return (
    <MobileScreen bg="surface">
      <TopBar title="我的文章" />

      <div className="flex gap-2 px-5 pt-2 pb-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`ARTmy-tab-${t.key}`}
            onClick={() => setFilter(t.key)}
            className={cn(
              'rounded-full px-3 py-1 text-[12px]',
              filter === t.key ? 'bg-[#1A1A1A] text-white' : 'bg-white text-[#9CA3AF]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <main data-testid="ARTmy" className="flex-1 overflow-y-auto px-5 pb-28">
        {isLoading ? (
          <div className="flex flex-col gap-2 pt-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl bg-surface-200" />
            ))}
          </div>
        ) : (articles ?? []).length === 0 ? (
          <div className="flex flex-col items-center gap-2 pt-20 text-[#9CA3AF]">
            <p className="text-[15px]">还没有文章</p>
            <button
              type="button"
              onClick={() => navigate('/article/create')}
              className="mt-2 rounded-full bg-[#19caad] px-6 py-2 text-white"
            >
              去写一篇
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-2 pt-2">
            {(articles ?? []).map((a) => {
              const badge = STATUS_BADGE[a.status]
              return (
                <li
                  key={a.id}
                  data-testid={`ARTmy-item-${a.id}`}
                  className="rounded-xl bg-white px-4 py-3"
                >
                  <button
                    type="button"
                    onClick={() =>
                      a.status === 'published'
                        ? navigate(`/article/${a.id}`)
                        : navigate(`/article/${a.id}/edit`)
                    }
                    className="block w-full text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className={cn('rounded-full px-2 py-0.5 text-[11px]', badge.cls)}>{badge.label}</span>
                      <span className="text-[11px] text-[#9CA3AF]">❤ {a.like_count}</span>
                      <span className="ml-auto text-[11px] text-[#9CA3AF]">{fmtTime(a.updated_at)}</span>
                    </div>
                    <p className="mt-1 truncate text-[15px] font-medium text-[#1A1A1A]">{a.title}</p>
                    {a.excerpt && <p className="mt-0.5 line-clamp-2 text-[12px] text-[#9CA3AF]">{a.excerpt}</p>}
                  </button>
                  <div className="mt-2 flex justify-end gap-3 text-[13px]">
                    <button
                      type="button"
                      data-testid={`ARTmy-edit-${a.id}`}
                      onClick={() => navigate(`/article/${a.id}/edit`)}
                      className="text-[#19caad]"
                    >
                      编辑
                    </button>
                    {a.status !== 'removed' && (
                      <button
                        type="button"
                        data-testid={`ARTmy-delete-${a.id}`}
                        onClick={() => setConfirmId(a.id)}
                        className="text-[#DC2626]"
                      >
                        下架
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </main>

      <button
        type="button"
        data-testid="ARTmy-fab"
        onClick={() => navigate('/article/create')}
        className="fixed bottom-[max(24px,env(safe-area-inset-bottom))] right-[calc(50%-201px+20px)] z-20 flex h-14 w-14 items-center justify-center rounded-full bg-[#19caad] text-white shadow-lg"
        aria-label="写文章"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 5v14M5 12h14" /></svg>
      </button>

      {confirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={() => setConfirmId(null)}>
          <div className="w-full max-w-[320px] rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-center text-[16px] font-semibold text-[#1A1A1A]">下架文章？</p>
            <p className="mt-2 text-center text-[12px] text-[#9CA3AF]">下架后将从首页消失，可在「已下架」中查看</p>
            <button
              type="button"
              className="mt-4 h-11 w-full rounded-lg bg-[#DC2626] text-[14px] font-semibold text-white disabled:opacity-50"
              disabled={deleteMut.isPending}
              onClick={async () => {
                await deleteMut.mutateAsync(confirmId)
                setConfirmId(null)
              }}
            >
              确认下架
            </button>
            <button type="button" className="mt-2 h-11 w-full rounded-lg bg-[#F6F7F8] text-[14px] font-medium text-[#1A1A1A]" onClick={() => setConfirmId(null)}>取消</button>
          </div>
        </div>
      )}
    </MobileScreen>
  )
}
